import { useState } from 'react';
import { useTable } from 'react-table';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import { Button, CopyToClipboard, OverlayTriggerTooltip } from '@utilities';
import { getCopyTableValue } from '@utilities/components/TableData/utilities/helpers';
import { GATES } from '@constants';

import { DISPLAY_AS } from '../../utilities/helpers';
import ModalTop2Box from '../ModalTop2Box';

import styles from './_index.module.scss';

const GridTable = ({
  columns,
  compareByGroups,
  data,
  displayAs = DISPLAY_AS.PERCENT.value,
  onTop2BoxApplied,
  question,
  surveyId,
}) => {
  const [isTop2BoxModalActive, setIsTop2BoxModalActive] = useState(false);
  const { footerGroups, getTableProps, headerGroups, prepareRow, rows } = useTable({
    columns,
    data,
  });

  const { client } = useStatsigClient();
  const canTop2Box = surveyId && client.checkGate(GATES.TOP_2_BOX);

  const alignLeftColumns = ['option_value', 'compare_by_breakout'];

  if (!data?.length) return null;

  return (
    <div>
      <div className={styles['grid-table-actions']}>
        <p className={styles['grid-table-label']}>{`Answer Options by ${
          displayAs === DISPLAY_AS.PERCENT.value ? DISPLAY_AS.PERCENT.label : DISPLAY_AS.COUNT.label
        }`}</p>
        <div className={styles['grid-table-actions-buttons']}>
          {canTop2Box && (
            <OverlayTriggerTooltip aria-label="Top2Box" content="Top 2 Box" key="top-2-box-btn">
              <Button
                onClick={() => {
                  setIsTop2BoxModalActive(true);
                }}
                text="Top 2 Box"
                variant="secondary"
              />
            </OverlayTriggerTooltip>
          )}
          <CopyToClipboard text="Copy Table" value={getCopyTableValue({ columns, data: rows })} />
        </div>
      </div>
      <table className={styles['grid-table']} {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  className={classNames({
                    [styles['grid-table-align-right']]: !alignLeftColumns.includes(column.id),
                    [styles['grid-table-compare-by-header']]: column.id === 'compare_by_breakout',
                    [styles['is-highlighted']]: column.variant === 'highlight',
                  })}
                  title={column.render('Header')}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {rows?.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={rowIndex}>
                {row?.cells?.map((cell) => {
                  const cellProps = cell.getCellProps({});
                  if (cell.column?.rowSpanBy) {
                    const rowSpanByValue = row.original[cell.column.rowSpanBy];
                    const rowCount = rows.filter(
                      ({ original }) => original[cell.column.rowSpanBy] === rowSpanByValue
                    ).length;

                    const firstRowIndex = rows.findIndex(
                      ({ original }) => original[cell.column.rowSpanBy] === rowSpanByValue
                    );
                    if (rowCount > 1 && rowIndex === firstRowIndex) {
                      cellProps.rowSpan = rowCount;
                    } else if (
                      rowCount > 1 &&
                      firstRowIndex < rowIndex &&
                      rowIndex < firstRowIndex + rowCount
                    ) {
                      // display none keeps 'nth-child' styles working
                      cellProps.style = { display: 'none' };
                    }
                  }

                  const isCompareByBreakout = cell.column.id === 'compare_by_breakout';
                  return (
                    <td
                      {...cellProps}
                      className={classNames({
                        [styles['grid-table-align-right']]: !alignLeftColumns.includes(
                          cell.column.id
                        ),
                        [styles['grid-table-compare-by-breakout']]: isCompareByBreakout,
                        [styles['is-highlighted']]: cell.column.variant === 'highlight',
                      })}
                    >
                      {isCompareByBreakout && (
                        <span
                          className={styles['grid-table-dot']}
                          style={{
                            backgroundColor: compareByGroups?.find(({ id, name }) =>
                              [id, name].includes(cell.value)
                            )?.color,
                          }}
                        />
                      )}
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          {footerGroups.map((group) => (
            <tr {...group.getFooterGroupProps()}>
              {group.headers.map((column) => (
                <td
                  {...column.getFooterProps()}
                  className={classNames({
                    [styles['grid-table-align-right']]: !alignLeftColumns.includes(column.id),
                  })}
                >
                  {column.render('Footer')}
                </td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
      <ModalTop2Box
        isActive={isTop2BoxModalActive}
        onApplied={onTop2BoxApplied}
        onClose={() => setIsTop2BoxModalActive(false)}
        question={question}
        surveyId={surveyId}
      />
    </div>
  );
};

GridTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  displayAs: PropTypes.string,
  onTop2BoxApplied: PropTypes.func,
  question: PropTypes.object,
  surveyId: PropTypes.number,
};

export default GridTable;
